import { AppWrapper } from "@helpers/context"; // import based on where you put it
import { MetaHead } from "@modules/shared/components";
import { RouteGuard } from "@modules/shared/components/RouteGuard";
import "@styles/global.scss";
import { AppProps } from "next/app";
import Router, { useRouter } from "next/router";
import React, { useEffect, useState } from "react";
import "react-loading-skeleton/dist/skeleton.css";
import "tailwindcss/tailwind.css";
import "react-spring-bottom-sheet/dist/style.css";
import useSound from "use-sound";
import dynamic from "next/dynamic";
import { GameplaySkeleton } from "@modules/gameplay/components/base";
import { ErrorComponent } from "@modules/shared/components/ErrorComponent";

const ApolloProviderApp = dynamic(
    () => import("@modules/shared/components/ApolloProviderApp"),
    {
        ssr: false,
    },
);

const FramerLazyMotion = dynamic(
    () => import("@modules/shared/components/FramerLazyMotion"),
    {
        ssr: false,
    },
);

function SetsGamify({ Component, pageProps }: AppProps): JSX.Element {
    const router = useRouter();
    const [isLoading, setIsLoading] = useState(false);
    const [showError, setShowError] = useState(false);

    const isAutoPlay = () => {
        const { pathname } = router;
        const nonAuthPaths = ["/quiz/", "/flow/", "/personalized_learning/"];
        if (nonAuthPaths.some((v) => pathname.includes(v))) {
            return false;
        }
        return true;
    };

    const [
        playGlobalBackgroundSound,
        { sound: globalBackgroundSound, stop: stopGlobalBackgroundSound },
    ] = useSound("/assets/sounds/Star-Night.mp3", {
        loop: true,
        volume: 0.25,
        autoplay: isAutoPlay(),
    });
    const user =
        typeof window !== "undefined" &&
        window &&
        window.navigator &&
        window.navigator.userAgent;
    useEffect(() => {
        if (
            user.toString().indexOf("UCBrowser") > -1 ||
            user.toString().indexOf("VivoBrowser") > -1
        ) {
            setShowError(true);
            setIsLoading(false);
        }
    }, []);

    useEffect(() => storePathValues, [router.asPath]);
    function storePathValues() {
        const storage = globalThis?.sessionStorage;
        if (!storage) return;
        // Set the previous path as the value of the current path.
        const prevPath = storage.getItem("currentPath") || "";
        storage.setItem("prevPath", prevPath);
        // Set the current path value by looking at the browser's location object.
        storage.setItem("currentPath", globalThis.location.pathname);
    }

    useEffect(() => {
        Router.events.on("routeChangeStart", () => {
            setIsLoading(true);
        });
        Router.events.on("routeChangeComplete", () => {
            setIsLoading(false);
        });
        Router.events.on("routeChangeError", () => {
            setIsLoading(false);
        });
    }, [Router]);

    useEffect(() => {
        if ("serviceWorker" in navigator) {
            window.addEventListener("load", function () {
                navigator.serviceWorker.register("/sw.js").then(
                    function (registration) {
                        console.log(
                            "Service Worker registration successful with scope: ",
                            registration.scope,
                        );
                    },
                    function (err) {
                        console.log(
                            "Service Worker registration failed: ",
                            err,
                        );
                    },
                );
            });
        }
    }, []);

    const { metadataTags } = pageProps;
    if (showError) return <ErrorComponent />;
    return (
        <>
            <MetaHead metadataTags={metadataTags} />

            {isLoading && <GameplaySkeleton />}

            <ApolloProviderApp>
                <AppWrapper>
                    {/* <ErrorBoundary> */}
                    <RouteGuard>
                        <FramerLazyMotion>
                            <Component
                                {...pageProps}
                                playGlobalBackgroundSound={
                                    playGlobalBackgroundSound
                                }
                                globalBackgroundSound={globalBackgroundSound}
                                stopGlobalBackgroundSound={
                                    stopGlobalBackgroundSound
                                }
                            />
                        </FramerLazyMotion>
                    </RouteGuard>
                    {/* </ErrorBoundary> */}
                </AppWrapper>
            </ApolloProviderApp>
        </>
    );
}

export default SetsGamify;
